import { useActiveCohort } from "@/lib/cohort-context";
import { ChevronDown, Layers } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export function CohortSwitcher({ tone = "dark" }: { tone?: "dark" | "light" }) {
  const { cohorts, cohortId, setCohortId, isLoading } = useActiveCohort();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (isLoading || cohorts.length === 0) return null;
  const active = cohorts.find(c => c.id === cohortId) || cohorts[0];

  const isDark = tone === "dark";
  const btn = isDark
    ? "bg-white/[0.04] hover:bg-white/[0.08] text-white border-white/[0.08]"
    : "bg-black/[0.04] hover:bg-black/[0.08] text-gray-900 border-black/[0.08]";
  const menu = isDark
    ? "bg-[#161616] border-white/[0.08] text-white"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <div className="relative" ref={ref} data-testid="cohort-switcher">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium transition ${btn}`}
        data-testid="cohort-switcher-button"
      >
        <Layers className="w-3.5 h-3.5 opacity-70" />
        <span className="max-w-[140px] truncate">{active?.name || "Cohort"}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-50" />
      </button>
      {open && (
        <div className={`absolute right-0 mt-1 w-64 rounded-md border shadow-lg z-50 ${menu}`}>
          <div className="py-1 max-h-72 overflow-y-auto">
            {cohorts.map(c => (
              <button
                key={c.id}
                onClick={() => { setCohortId(c.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center justify-between ${c.id === active?.id ? "font-semibold" : ""}`}
                data-testid={`cohort-option-${c.slug}`}
              >
                <span className="truncate">{c.name}</span>
                {c.id === active?.id && <span className="text-[10px] opacity-60">active</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
